'use client';

import clsx from 'classnames';
import Checkmark from 'icons/lib/check_light.svg';
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react';

import styles from './TextField.module.scss';
import { TextFieldProps } from './types';

export const TextField = forwardRef(
  (
    {
      fullWidth = false,
      prefix,
      suffix,
      className,
      inputClassName,
      value,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      onKeyUp,
      hasError,
      isValid,
      autoFocus,
      ...props
    }: TextFieldProps,
    externalRef: ForwardedRef<HTMLInputElement>
  ) => {
    const ref = useRef<HTMLInputElement | null>(null);
    const [hasFocus, setHasFocus] = useState(false);
    const [hasValue, setHasValue] = useState(!!value || !!ref?.current?.value);

    const containerClassNames = clsx(
      `TextField`,
      styles.textFieldWrapper,
      {
        [styles.textFieldFullWidth]: !!fullWidth,
      },
      className
    );

    const labelClassNames = clsx(styles.textFieldInputLabel, {
      [styles['textFieldInputLabel--active']]: hasFocus || hasValue,
    });

    const inputClassNames = clsx(styles.textFieldInputField, inputClassName);

    useEffect(() => {
      setHasValue(!!value || !!ref?.current?.value);
    }, [value, ref?.current?.value, props.type]);

    return (
      <div className={containerClassNames}>
        {props.label || props.placeholder ? (
          <label className={labelClassNames}>
            {props.label || props.placeholder}
          </label>
        ) : null}
        <div
          className={clsx(styles.textFieldInputWrapper, {
            [styles['textFieldInputWrapper--hasError']]: !!hasError,
          })}
        >
          {prefix ? (
            <span className={styles.textFieldInputPrefix}>{prefix}</span>
          ) : null}
          <input
            ref={(refObject) => {
              ref.current = refObject;

              if (typeof externalRef === 'function') externalRef(refObject);
              else if (externalRef !== null) externalRef.current = refObject;
            }}
            className={inputClassNames}
            {...props}
            placeholder={undefined}
            value={value}
            onChange={onChange}
            onFocus={(event) => {
              setHasFocus(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              setHasFocus(false);
              onBlur?.(event);
            }}
            autoFocus={autoFocus}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
          />
          {isValid && !hasError ? (
            <span className={styles.textFieldInputValidationIcon}>
              <Checkmark />
            </span>
          ) : null}
          {suffix ? (
            <span className={styles.textFieldInputSuffix}>{suffix}</span>
          ) : null}
        </div>
      </div>
    );
  }
);

TextField.displayName = 'TextField';
